import React, { useContext, useMemo, useRef } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Icon, Layout } from '@oneaudi/unified-web-components';
import type { AudiHeaderDealerProps } from '../../interfaces/header-components.interfaces';
import {
  HeaderStyledDealerDisplay,
  HeaderStyledDealerTextWrapper,
  HeaderStyledDealerLink,
  HeaderStyledDealerName,
  HeaderStyledDealerButton,
  HeaderStyledDealerLabel,
} from './AudiHeaderDealerContextStyles';
import AudiHeaderAdditionalDealerData from './AudiHeaderAdditionalDealerData';
import { truncate } from '../../utils/string-utils';
import { messages } from '../../i18n/messages';
import { DealerContext } from '../../context';
import { getKeyboardFocusableElements, keyTypes, navigate, trapFocus } from '../../utils/a11y';
import useKeyboard from '../../hooks/use-keyboars-events';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';
import { SYS_SPACE_FIXED_150 } from '@oneaudi/unified-web-common';
import { PoiIcon } from '../Icons/PoiIcon';
import {
  SYS_COLOR_CANVAS_100_DARK,
  SYS_COLOR_CANVAS_100_LIGHT,
} from '@oneaudi/unified-web-alpha-common';

const DealerIconLayout = ({ dealerIcon }: { dealerIcon: React.ReactNode }) => {
  return (
    <Layout align="flex-start" as="div" margin={[0, SYS_SPACE_FIXED_150, 0, 0]}>
      {dealerIcon}
    </Layout>
  );
};

const AudiHeaderDealerContext: React.FC<AudiHeaderDealerProps> = ({
  isMobileView = false,
  headerNavigationItemsAmount,
  onClickDealerContext,
  showDealerAdditionalData,
  showDealerSearch,
}) => {
  const dealerLabel = useI18n(messages.dealer_label);
  const showDealerInfo = useI18n(messages.dealer_info);
  const hideDealerInfo = useI18n(messages.hide_dealer_info);
  const dealerButtonLabel = showDealerAdditionalData ? hideDealerInfo : showDealerInfo;
  const refDealerData = useRef<HTMLDivElement>(null);
  const arrowIcon = showDealerAdditionalData ? (
    <Icon name="arrow-up" size="s" />
  ) : (
    <Icon name="arrow-down" size="s" />
  );

  const dealerIcon = showDealerAdditionalData ? (
    <Icon name="cancel" size="s" />
  ) : (
    <PoiIcon
      color={process.env.IS_LEGACY ? SYS_COLOR_CANVAS_100_DARK : SYS_COLOR_CANVAS_100_LIGHT}
      active
    />
  );

  const { dealerData } = useContext(DealerContext);
  const isLegacy = Boolean(process.env.IS_LEGACY);

  const focusedElements = useMemo(() => {
    if (refDealerData.current && showDealerAdditionalData) {
      const focusedElements = getKeyboardFocusableElements(refDealerData.current, true);
      // set initial focus on first element
      requestAnimationFrame(() => {
        setTimeout(() => {
          focusedElements[0].focus();
        }, 200);
      });
      return focusedElements;
    }
    return [];
  }, [refDealerData, showDealerAdditionalData]);

  useKeyboard(
    'keydown',
    (event) => {
      if (refDealerData?.current && focusedElements?.length > 0) {
        trapFocus(focusedElements, event);
        if (event.key === keyTypes.ArrowDown || event.key === keyTypes.ArrowUp) {
          navigate(
            refDealerData.current,
            event.key,
            keyTypes.ArrowDown,
            keyTypes.ArrowUp,
            NAVIGATION_CLASSNAME,
          );
        }
      }
    },
    refDealerData,
  );

  return (
    <>
      <HeaderStyledDealerDisplay
        isMobileView={isMobileView}
        headerNavigationItemsAmount={headerNavigationItemsAmount}
      >
        <HeaderStyledDealerButton
          id="dealerButton"
          isMobileView={isMobileView}
          title={dealerButtonLabel}
          aria-label={dealerButtonLabel}
          type="button"
          onClick={() => {
            const shouldOpen = !showDealerAdditionalData;
            const currentScrollY = window.scrollY;
            const isScrolled = currentScrollY > 1;

            if (shouldOpen && isScrolled) {
              // Opening and page is scrolled - scroll to top first
              const handleScrollEnd = () => {
                const scrollY = window.scrollY;

                if (scrollY === 0) {
                  window.removeEventListener('scroll', handleScrollEnd);
                  onClickDealerContext();
                }
              };

              window.addEventListener('scroll', handleScrollEnd);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              // Closing or already at top - toggle immediately
              onClickDealerContext();
            }
          }}
          data-testid="dealer-data-toggle"
          className="navigation"
        >
          {isLegacy ? (
            <>
              {!isMobileView && <DealerIconLayout dealerIcon={dealerIcon} />}
              <HeaderStyledDealerTextWrapper>
                {isMobileView && <DealerIconLayout dealerIcon={dealerIcon} />}
                <HeaderStyledDealerLink>
                  <HeaderStyledDealerLabel isMobileView={isMobileView}>
                    <span>{dealerLabel}</span>
                  </HeaderStyledDealerLabel>
                  <HeaderStyledDealerName isMobileView={isMobileView}>
                    {!isMobileView
                      ? truncate(dealerData?.additionalData?.displayName || dealerData?.name)
                      : dealerData?.additionalData?.displayName ||
                        dealerData?.name ||
                        'Name not found'}
                  </HeaderStyledDealerName>
                </HeaderStyledDealerLink>
              </HeaderStyledDealerTextWrapper>
              {isMobileView && (
                <Layout justify="flex-end" align="flex-end">
                  {arrowIcon}
                </Layout>
              )}
            </>
          ) : (
            <>
              <HeaderStyledDealerTextWrapper>
                <Layout align="flex-start" as="div" margin={[0, '12px', 0, 0]}>
                  {!isMobileView ? dealerIcon : <Icon name="poi-active" size="s" />}
                </Layout>
                <HeaderStyledDealerLink>
                  <HeaderStyledDealerLabel isMobileView={isMobileView}>
                    <span>{dealerLabel}</span>
                  </HeaderStyledDealerLabel>
                  <HeaderStyledDealerName isMobileView={isMobileView}>
                    {!isMobileView
                      ? truncate(dealerData?.additionalData?.displayName || dealerData?.name)
                      : dealerData?.additionalData?.displayName ||
                        dealerData?.name ||
                        'Name not found'}
                  </HeaderStyledDealerName>
                </HeaderStyledDealerLink>
              </HeaderStyledDealerTextWrapper>
              {isMobileView && (
                <Layout justify="flex-end" align="flex-end">
                  {arrowIcon}
                </Layout>
              )}
            </>
          )}
        </HeaderStyledDealerButton>
        <AudiHeaderAdditionalDealerData
          open={showDealerAdditionalData}
          isMobileView={isMobileView}
          refDealerData={refDealerData}
          showDealerSearch={showDealerSearch}
        />
      </HeaderStyledDealerDisplay>
    </>
  );
};

export default AudiHeaderDealerContext;
